import { Component, Element, h, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { HEADLINE_BRAND_SCHEME_CONFIG, HEADLINE_BRAND_SCHEME_MAP } from "./constants/headline-brand-constants";
import {
  HeadlineBrandSchemeDefinition,
  HeadlineBrandSchemeName,
  HeadlineBrandTag
} from "./constants/headline-brand-types";
import { HeadlineBrandSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import { HEADLINE_BRAND_DEFAULT_ALIGNMENT, HeadlineBrandAlignment } from "../../constants/global-constants";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 */
@Component({
  tag: "eon-ui-headline-brand",
  styleUrl: "headline-brand.scss",
  assetsDirs: ["assets-headline-brand"],
  shadow: true
})
export class HeadlineBrand
  implements
    ColorableComponent<HeadlineBrandSchemeConfiguration<HeadlineBrandSchemeDefinition>>,
    ContainerBreakpointComponent {
  // @InitLogging() sentryTransaction;
  schemeConfig: HeadlineBrandSchemeConfiguration<HeadlineBrandSchemeDefinition> = HEADLINE_BRAND_SCHEME_CONFIG;
  schemeMap = HEADLINE_BRAND_SCHEME_MAP;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];

  @State() activeScheme: HeadlineBrandSchemeDefinition;

  @Element() hostElement: HTMLEonUiHeadlineBrandElement;
  @State() processedText: string;
  @State() seoHeadlineBrandTag: string;
  @State() infoIconComponent: HTMLEonUiInfoIconElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: HeadlineBrandSchemeName;

  /**
   * Container based size
   */
  @Prop({ reflect: true }) sizeContainerBased: boolean = false;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline-brand,<br>
   *     e.g. if headline-html-tag="h1" the HTML-Tag "H1" is used for the headline-brand.<br>
   *     **IMPORTANT**: This property **does not** affect the styles!
   * @example <eon-ui-headline-brand size="h5" headline-html-tag="h1" text="Better Tomorrow"></eon-ui-headline-brand>
   */
  @Prop() headlineHtmlTag: HeadlineBrandTag;

  /**
   * Alignment of the headline-brand.
   * @example <eon-ui-headline-brand size="h3" alignment="center" text="Better Tomorrow"></eon-ui-headline-brand>
   */
  @Prop() alignment: HeadlineBrandAlignment = HEADLINE_BRAND_DEFAULT_ALIGNMENT;

  /**
   * Ignore alignment property
   */
  @Prop() ignoreAlignment: boolean = false;

  /**
   * Text content of the headline-brand
   *
   * @example <eon-ui-headline-brand size="h1" text="Better Tomorrow"></eon-ui-headline-brand>
   */
  @Prop() text: string;

  /**
   * A boolean property that can be used to disable line breaks.
   */
  @Prop() disableLineWrap: boolean = false;

  /**
   * A boolean property that can be used to enable line hyphenation.<br>
   *   Important!: If you have activated hyphenation, note that you must then also set the language, see also the property "language".
   */
  @Prop() hyphenation: boolean = false;

  /**
   * if you enabled hyphenation, it is important that the language for the headline-brand is defined.<br>
   *  There is an automatism that finds out in which language the HTML document exists. If no lang attribute is specified at the parent tags, then the language must be set manually.
   *  <br>Look <a href="https://www.w3schools.com/tags/ref_language_codes.asp" target="_blank">here</a> for a List of available language code.
   */
  @Prop() language: string;

  componentWillLoad() {
    this.applyScheme();
    this.updateProcessedText();
    this.handleHeadlineBrandTag();
    this.initContainerBreakpoints();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  /**
   * Executes whenever the text property changes. Replaced the specified linewrap symbol with <br />.
   */
  @Watch("text")
  updateProcessedText() {
    const slashN = "\\n";
    if (this.text && !this.disableLineWrap) {
      this.processedText = this.text.split(slashN).join("<br />");
    } else {
      this.processedText = this.text;
    }
  }

  @Watch("headlineHtmlTag")
  handleHeadlineBrandTag() {
    this.seoHeadlineBrandTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, "h1");
  }

  render() {
    const TagForHeadlineBrand = `${this.seoHeadlineBrandTag.toLowerCase()}` as keyof JSX.IntrinsicElements;
    return (
      <TagForHeadlineBrand
        lang={this.language}
        class={pfx({
          ["heading-brand"]: true,
          ["info-icon-display"]: this.infoIconComponent,
          [this.alignment]: this.alignment,
          ["ignore-alignment"]: this.ignoreAlignment,
          ["hyphenation"]: this.hyphenation,
          ["container-based-size"]: this.sizeContainerBased
        })}
      >
        <span innerHTML={this.processedText}></span>
      </TagForHeadlineBrand>
    );
  }
}
